/**
 * Media Presentation Component - Supports both DSL (Deck Sales Letter) and VSL (Video Sales Letter)
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel';
import { ArrowRight, CheckCircle2, ArrowDown, Play } from 'lucide-react';
import { MediaConfig, DSLSlide } from '../config/types';

interface MediaPresentationProps {
  config: MediaConfig;
  onStartAssessment: () => void;
}

/**
 * DSL (Deck Sales Letter) Component
 */
function DSLPresentation({
  slides,
  onStartAssessment,
}: {
  slides: DSLSlide[];
  onStartAssessment: () => void;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(1);

  // Listen for custom event from slide buttons
  useEffect(() => {
    const handleStartAssessment = (event: Event) => {
      try {
        if (typeof onStartAssessment === 'function') {
          onStartAssessment();
        }
      } catch (error) {
        console.error('Error in startAssessment handler:', error);
      }
    };
    window.addEventListener('startAssessment', handleStartAssessment);
    return () => {
      window.removeEventListener('startAssessment', handleStartAssessment);
    };
  }, [onStartAssessment]);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);


  if (!slides || slides.length === 0) {
    console.error('DSLPresentation: No slides provided');
    return (
      <div className="w-full max-w-6xl mx-auto text-center p-12">
        <p className="text-slate-300 text-xl">No slides available</p>
      </div>
    );
  }

  // Validate slides structure
  const validSlides = slides.filter((slide, index) => {
    if (!slide || (typeof slide !== 'object')) {
      console.error(`DSLPresentation: Invalid slide at index ${index}:`, slide);
      return false;
    }
    return true;
  });

  if (validSlides.length === 0) {
    console.error('DSLPresentation: No valid slides found');
    return (
      <div className="w-full max-w-6xl mx-auto text-center p-12">
        <p className="text-red-400 text-xl">No valid slides found</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto flex flex-col items-center justify-center min-h-[400px] md:min-h-[600px]">
      <Carousel setApi={setApi} className="w-full">
        <CarouselContent className="-ml-2 md:-ml-4">
          {validSlides.map((slide, index) => {
            // Consistent slate background for all slides
            try {
              return (
                <CarouselItem key={index} className="pl-2 md:pl-4 basis-full flex items-center justify-center">
                  <div className="min-h-[300px] md:min-h-[500px] w-full max-w-5xl flex flex-col items-center justify-center p-4 md:p-12 text-center rounded-lg border-2 border-slate-700 bg-slate-800 text-white shadow-2xl">
                    <div className="w-full">
                      {slide.headline !== null && slide.headline !== undefined && (
                        <div className="mb-6">{slide.headline}</div>
                      )}
                      {slide.body !== null && slide.body !== undefined && <div className="mt-4">{slide.body}</div>}
                      {/* Add down arrow on last slide only if it doesn't already have one in its body */}
                      {index === validSlides.length - 1 && !slide.body && (
                        <div className="mt-8 flex justify-center">
                          <svg className="w-12 h-12 md:w-16 md:h-16 text-orange-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </CarouselItem>
              );
            } catch (error) {
              console.error(`Error rendering slide ${index}:`, error);
              return (
                <CarouselItem key={index} className="pl-2 md:pl-4 basis-full flex items-center justify-center">
                  <div className="min-h-[300px] md:min-h-[500px] w-full max-w-5xl flex flex-col items-center justify-center p-4 md:p-12 text-center rounded-lg border-2 border-slate-700 bg-slate-800 text-white shadow-2xl">
                    <p className="text-red-400">Error loading slide {index + 1}</p>
                  </div>
                </CarouselItem>
              );
            }
          })}
        </CarouselContent>
        <CarouselPrevious className="text-white border-blue-600 bg-blue-900/80 hover:bg-blue-800 hover:border-amber-400 left-2 md:left-4 shadow-lg" />
        <CarouselNext className="text-white border-blue-600 bg-blue-900/80 hover:bg-blue-800 hover:border-amber-400 right-2 md:right-4 shadow-lg" />
      </Carousel>
      
      <div className="flex justify-center items-center gap-4 mt-6">
        <span className="text-slate-300 text-sm md:text-base font-medium">
          Slide {current} of {slides.length}
        </span>
      </div>
    </div>
  );
}

/**
 * VSL (Video Sales Letter) Component
 */
function VSLPresentation({
  videoUrl,
  thumbnailUrl,
  autoplay = false,
  onStartAssessment,
}: {
  videoUrl: string;
  thumbnailUrl?: string;
  autoplay?: boolean;
  onStartAssessment: () => void;
}) {
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = () => {
    setIsPlaying(true);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
        {!isPlaying && thumbnailUrl && (
          <div
            className="absolute inset-0 bg-cover bg-center cursor-pointer"
            style={{ backgroundImage: `url(${thumbnailUrl})` }}
            onClick={handlePlay}
          >
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <button
                onClick={handlePlay}
                className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
              >
                <Play className="w-10 h-10 text-black ml-1" fill="black" />
              </button>
            </div>
          </div>
        )}
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          className="w-full h-full"
          autoPlay={autoplay}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
      </div>
      
      {!isPlaying && (
        <div className="mt-6 text-center">
          <p className="text-white text-lg mb-4">
            Watch the video above, then click below to get started
          </p>
          <Button
            onClick={onStartAssessment}
            size="lg"
            className="bg-orange-500 hover:bg-orange-600 text-white text-lg px-8 py-6"
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Main Media Presentation Component
 */
export function MediaPresentation({
  config,
  onStartAssessment,
}: MediaPresentationProps) {
  try {
    if (!config) {
      console.error('MediaPresentation: config is undefined');
      return (
        <div className="w-full max-w-6xl mx-auto text-center p-12">
          <p className="text-red-400 text-xl">Media configuration is missing</p>
        </div>
      );
    }

    if (config.type === 'dsl') {
      if (!config.dsl) {
        console.error('MediaPresentation: DSL config is missing');
        return (
          <div className="w-full max-w-6xl mx-auto text-center p-12">
            <p className="text-red-400 text-xl">DSL configuration is missing</p>
          </div>
        );
      }
      
      if (!config.dsl.slides || !Array.isArray(config.dsl.slides) || config.dsl.slides.length === 0) {
        console.error('MediaPresentation: DSL slides are missing or invalid', config.dsl);
        return (
          <div className="w-full max-w-6xl mx-auto text-center p-12">
            <p className="text-red-400 text-xl">DSL slides are missing or invalid</p>
          </div>
        );
      }

      return (
        <DSLPresentation
          slides={config.dsl.slides}
          onStartAssessment={onStartAssessment}
        />
      );
    }

    if (config.type === 'vsl') {
      if (!config.vsl?.videoUrl) {
        console.error('MediaPresentation: VSL videoUrl is missing');
        return (
          <div className="w-full max-w-6xl mx-auto text-center p-12">
            <p className="text-red-400 text-xl">VSL video URL is missing</p>
          </div>
        );
      }

      return (
        <VSLPresentation
          videoUrl={config.vsl.videoUrl}
          thumbnailUrl={config.vsl.thumbnailUrl}
          autoplay={config.vsl.autoplay}
          onStartAssessment={onStartAssessment}
        />
      );
    }

    return (
      <div className="w-full max-w-6xl mx-auto text-center p-12">
        <p className="text-white text-xl">Media configuration not found</p>
      </div>
    );
  } catch (error) {
    console.error('Error in MediaPresentation:', error);
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
    return (
      <div className="w-full max-w-6xl mx-auto text-center p-12">
        <p className="text-red-400 text-xl">Error loading media presentation</p>
        <p className="text-red-300 text-sm mt-2">{error instanceof Error ? error.message : 'Unknown error'}</p>
      </div>
    );
  }
}
